import * as React from "react"

import {
  NewEngagementFieldError,
  type CreateClientInput,
  type NewEngagementField,
  type UserId,
} from "../clients-data"

export interface NewEngagementDraft {
  name: string
  primaryClientContactId: UserId | null
  notes: string
}

export type NewEngagementFieldErrors = Record<NewEngagementField, string | null>

const EMPTY_NEW_ENGAGEMENT_DRAFT: NewEngagementDraft = {
  name: "",
  primaryClientContactId: null,
  notes: "",
}

const NO_NEW_ENGAGEMENT_FIELD_ERRORS: NewEngagementFieldErrors = {
  name: null,
  primaryClientContact: null,
}

export interface NewEngagementDraftState {
  draft: NewEngagementDraft
  fieldErrors: NewEngagementFieldErrors
  isCreating: boolean
  canSubmit: boolean
  changeName: (name: string) => void
  changePrimaryClientContact: (primaryClientContactId: UserId | null) => void
  changeNotes: (notes: string) => void
  submitDraft: () => Promise<void>
}

export function useNewEngagementDraft(
  onSubmit: (draft: CreateClientInput) => Promise<void>,
): NewEngagementDraftState {
  const [draft, setDraft] = React.useState(EMPTY_NEW_ENGAGEMENT_DRAFT)
  const [fieldErrors, setFieldErrors] = React.useState(NO_NEW_ENGAGEMENT_FIELD_ERRORS)
  const [isCreating, setIsCreating] = React.useState(false)

  const canSubmit = draft.name.trim().length > 0 && !isCreating

  const changeName = (name: string) => {
    setDraft((currentDraft) => ({ ...currentDraft, name }))
    setFieldErrors((currentFieldErrors) => ({ ...currentFieldErrors, name: null }))
  }

  const changePrimaryClientContact = (primaryClientContactId: UserId | null) => {
    setDraft((currentDraft) => ({ ...currentDraft, primaryClientContactId }))
    setFieldErrors((currentFieldErrors) => ({ ...currentFieldErrors, primaryClientContact: null }))
  }

  const changeNotes = (notes: string) => {
    setDraft((currentDraft) => ({ ...currentDraft, notes }))
  }

  const startFreshDraft = () => {
    setDraft(EMPTY_NEW_ENGAGEMENT_DRAFT)
    setFieldErrors(NO_NEW_ENGAGEMENT_FIELD_ERRORS)
  }

  const showFieldError = (fieldError: NewEngagementFieldError) => {
    if (fieldError.field === "primaryClientContact") {
      setDraft((currentDraft) => ({ ...currentDraft, primaryClientContactId: null }))
    }
    setFieldErrors((currentFieldErrors) => ({
      ...currentFieldErrors,
      [fieldError.field]: fieldError.message,
    }))
  }

  const submitDraft = async () => {
    if (!canSubmit) return
    setIsCreating(true)
    try {
      await onSubmit({
        name: draft.name.trim(),
        primaryClientContactId: draft.primaryClientContactId,
        notes: draft.notes.trim() || null,
      })
      startFreshDraft()
    } catch (createError) {
      if (createError instanceof NewEngagementFieldError) showFieldError(createError)
    } finally {
      setIsCreating(false)
    }
  }

  return {
    draft,
    fieldErrors,
    isCreating,
    canSubmit,
    changeName,
    changePrimaryClientContact,
    changeNotes,
    submitDraft,
  }
}
